
import { useToast } from "@/hooks/use-toast";
import ImageUploadControls from "./image-upload/ImageUploadControls";
import ImagePreview from "./image-upload/ImagePreview";
import { useImageUpload } from "./image-upload/useImageUpload";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FileObject } from "@supabase/storage-js";

interface ImageUploadSectionProps {
  onImageSelected: (url: string) => void;
  currentImageUrl?: string;
  onImageRemove?: () => void;
}

const ImageUploadSection = ({ onImageSelected, currentImageUrl, onImageRemove }: ImageUploadSectionProps) => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<FileObject[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  const {
    isUploading,
    isGenerating,
    imagePrompt,
    previewUrl,
    imageUrl,
    setImagePrompt,
    handleImageUpload,
    handleGenerateImage,
    handleAcceptGeneratedImage,
    handleSaveToGallery,
    handleRejectGeneratedImage,
    clearImageUrl,
  } = useImageUpload(onImageSelected, currentImageUrl);

  const fetchGalleryImages = async () => {
    setLoading(true);
    try {
      const { data: files, error } = await supabase
        .storage
        .from('article-images')
        .list();

      if (error) {
        throw error;
      }
      setGalleryImages(files);
    } catch (error) {
      console.error("Error fetching gallery images:", error);
      toast({
        title: "Error",
        description: "Failed to load gallery images",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (galleryOpen) {
      fetchGalleryImages();
    }
  }, [galleryOpen]);

  const getImageUrl = (fileName: string) => {
    const { data } = supabase.storage
      .from('article-images')
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const selectGalleryImage = (image: FileObject) => {
    const imageUrl = getImageUrl(image.name);
    onImageSelected(imageUrl);
    setGalleryOpen(false);
  };

  const handleRemoveImage = () => {
    clearImageUrl();
    if (onImageRemove) {
      onImageRemove();
    }
    toast({
      title: "Image removed",
      description: "The image has been removed from the article",
    });
  };

  return (
    <div>
      <label htmlFor="image" className="block text-sm font-medium mb-2">
        Image
      </label>
      <div className="space-y-4">
        <div className="flex gap-2">
          <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex-1" disabled={isUploading || isGenerating}>
                <ImageIcon className="mr-2 h-4 w-4" />
                Choose from Gallery
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Image Gallery</DialogTitle>
                <DialogDescription>
                  Select an image from your gallery to use in your newsletter
                </DialogDescription>
              </DialogHeader>
              
              {loading ? (
                <div className="p-8 text-center">Loading images...</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
                  {galleryImages.map((image) => (
                    <div 
                      key={image.id} 
                      className="relative aspect-square overflow-hidden rounded-md border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors"
                      onClick={() => selectGalleryImage(image)}
                    >
                      <img
                        src={getImageUrl(image.name)}
                        alt={image.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {galleryImages.length === 0 && !loading && (
                <div className="p-8 text-center text-gray-500">
                  No images found in your gallery
                </div>
              )}
            </DialogContent>
          </Dialog>
          
          {currentImageUrl && (
            <Button 
              variant="outline" 
              size="icon" 
              className="flex-shrink-0" 
              onClick={handleRemoveImage}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        <ImageUploadControls
          imagePrompt={imagePrompt}
          isUploading={isUploading}
          isGenerating={isGenerating}
          onPromptChange={setImagePrompt}
          onUpload={handleImageUpload}
          onGenerate={handleGenerateImage}
        />
        
        <ImagePreview
          previewUrl={previewUrl}
          imageUrl={imageUrl}
          isUploading={isUploading}
          onAccept={handleAcceptGeneratedImage}
          onSaveToGallery={handleSaveToGallery}
          onReject={handleRejectGeneratedImage}
        />
      </div>
    </div>
  );
};

export default ImageUploadSection;
